import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { DokumentyDokumentDto } from '@gov-nx/api';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useDokladyContext } from '@gov-nx/module/page';
import {
	Button,
	CopyButton,
	ErrorStatusBanner,
	Icon,
	LayoutGap,
	Space,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { DeletePrompt } from './DeletePrompt';

interface OwnDocumentsListProps {
	code: PageCode;
}

export const OwnDocumentsList = ({ code }: OwnDocumentsListProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const navigate = useNavigate();

	const [documentToDelete, setDocumentToDelete] = useState<
		DokumentyDokumentDto | undefined
	>(undefined);
	const {
		ownDocuments,
		onOwnDocumentDelete,
		documentPreviewQuery,
		ownDocumentsError,
	} = useDokladyContext();

	if (ownDocumentsError) {
		return <ErrorStatusBanner code={code} />;
	}
	return (
		<>
			<section>
				<h2 className="text-body-l!">{tsn('vlastni-doklady.titulek')}</h2>

				<ul className="gov-list--plain mt-(--templates-margin-l)!">
					{ownDocuments.map((document) => {
						if (!document.dokumentId) return null;

						return (
							<li
								key={document.dokumentId}
								className="-mx-5 border-b border-(--border-subtlest) [ last-of-type:border-b-0 ] [ md:mx-0 ]">
								<div className="relative p-(--templates-margin-l)">
									<div className="flex justify-between items-center gap-2 mb-(--spacing-xs) [ md:mb-(--spacing-s) ]">
										<div className="flex gap-2">
											<h3 className="text-body-l-bold! text-(--text-primary-color) break-all">
												{document.nazev ?? ''}
											</h3>
											<Link
												to={pageRouteResolver('profil.doklady.vlastni-doklad', {
													documentId: document.dokumentId,
												})}
												className="flex items-center">
												<Icon
													name="pencil-square"
													type="templates"
													size="m"
													aria-label={tsn('vlastni-doklady.akce.wcag.upravit')}
												/>
											</Link>
										</div>

										<Button
											aria-label={tsn('vlastni-doklady.akce.wcag.odstranit')}
											color="primary"
											type="base"
											size="m"
											onClick={() => setDocumentToDelete(document)}
											className="relative -mr-[16px]!"
											iconStart={
												<Icon
													type="components"
													name="x-lg"
												/>
											}
										/>
									</div>
									<LayoutGap
										gap="s"
										className="md:inline!">
										{document.soubory && document.soubory?.length > 0 && (
											<span className="text-(--text-secondary) md:mr-4!">
												{tsn('vlastni-doklady.klic.nazev-souboru')}
												<Space />
												{document.soubory.map((soubor, i) => {
													if (!soubor.souborId) {
														return (
															// Edge case that shouldn't ever occur
															// eslint-disable-next-line react/no-array-index-key
															<span key={i}>
																{tsn('vlastni-doklady.nahled-neni')}
															</span>
														);
													}

													return (
														<span key={soubor.souborId}>
															<Button
																aria-label={tsn(
																	'vlastni-doklady.akce.wcag.otevrit'
																)}
																color="primary"
																type="link"
																isDisabled={!document.dokumentId}
																isLoading={
																	documentPreviewQuery.documentPreviewQuery
																		.isFetching &&
																	documentPreviewQuery.downloadingId ===
																		soubor.souborId
																}
																onClick={() =>
																	document.dokumentId &&
																	documentPreviewQuery.fetchFile(
																		document.dokumentId,
																		soubor.souborId!,
																		(blob) =>
																			window.open(URL.createObjectURL(blob))
																	)
																}>
																<span className="truncate max-w-[295px] md:max-w-[560px]">
																	{soubor.nazev}
																</span>
															</Button>
															{i + 1 < document.soubory!.length && ', '}
														</span>
													);
												})}
											</span>
										)}

										{document.cisloDokumentu && (
											<span className="text-(--text-secondary) md:mr-4!">
												{tsn('vlastni-doklady.klic.cislo-dokumentu')}
												<Space />
												<strong className="text-(--text-primary) break-all">
													{document.cisloDokumentu}
												</strong>
												<CopyButton
													label={tsn('akce.kopirovat-cislo-dokladu')}
													value={document.cisloDokumentu}
												/>
											</span>
										)}

										<span className="text-(--text-secondary) whitespace-nowrap md:mr-4!">
											{tsn('vlastni-doklady.klic.platnost-od')}
											<Space />
											<strong className="text-(--text-primary)">
												{document.platnostOd
													? toDateReadable(document.platnostOd)
													: tsn('vlastni-doklady.hodnota.nevyplneno')}
											</strong>
										</span>

										<span className="text-(--text-secondary) whitespace-nowrap md:mr-4!">
											{tsn('vlastni-doklady.klic.platnost-do')}
											<Space />
											<strong className="text-(--text-primary)">
												{document.platnostDo
													? toDateReadable(document.platnostDo)
													: tsn('vlastni-doklady.hodnota.nevyplneno')}
											</strong>
										</span>

										{document.komentar && (
											<span className="text-(--text-secondary)">
												{tsn('vlastni-doklady.klic.komentar')}
												<Space />
												<strong className="text-(--text-primary) break-all">
													{document.komentar}
												</strong>
											</span>
										)}
									</LayoutGap>
								</div>
							</li>
						);
					})}
				</ul>

				<DeletePrompt
					code={code}
					documentToDelete={documentToDelete}
					onClose={() => setDocumentToDelete(undefined)}
					onDelete={() => {
						documentToDelete?.dokumentId &&
							onOwnDocumentDelete({
								id: documentToDelete.dokumentId,
								permanent: false,
							});
						setDocumentToDelete(undefined);
					}}
				/>
			</section>
			<Button
				onClick={() =>
					navigate(
						pageRouteResolver('profil.doklady.vlastni-doklad', {
							documentId: '',
						})
					)
				}
				color="primary"
				aria-label={tsn('vlastni-doklady.akce.pridat-novy-dokument')}
				type="outlined"
				size="m"
				isExpandedMobile
				iconStart={
					<Icon
						name="plus-circle"
						type="templates"
					/>
				}>
				{tsn('vlastni-doklady.akce.pridat-novy-dokument')}
			</Button>
		</>
	);
};
